import styles from "./MaritimeForecastRenderer.module.css";

export type ForecastWarningEntry = {
  areaName: string;
  /** Undefined means no active warning for this area. */
  description?: string;
};

export type ForecastAreaRow = {
  areaName: string;
  text: string;
};

export type ForecastPeriodRows = {
  header: string;
  rows: ForecastAreaRow[];
};

export type ForecastDay1Temp = {
  areaName: string;
  min: number;
  max: number;
  sunrise?: string;
  sunset?: string;
};

export type ForecastDay2Temp = {
  areaName: string;
  min: number;
  max: number;
};

export type ForecastWaterTemp = {
  areaName: string;
  value: number;
};

export type MaritimeForecastRendererProps = {
  issuedLine?: string;
  footerLines?: [string, string];
  synopsis: string;
  warnings: ForecastWarningEntry[];
  noWarningsText: string;
  hasWarnings: boolean;
  windGustSectionHeader: string;
  windGustPeriods: ForecastPeriodRows[];
  cloudWeatherSectionHeader: string;
  cloudWeatherPeriods: ForecastPeriodRows[];
  visibilitySectionHeader: string;
  visibilityPeriods: ForecastPeriodRows[];
  day1TempHeader: string;
  day1Temps: ForecastDay1Temp[];
  day2TempHeader: string;
  day2Temps: ForecastDay2Temp[];
  waterTempHeader: string;
  waterTemps: ForecastWaterTemp[];
};

const MaritimeForecastRenderer = ({
  issuedLine,
  footerLines,
  synopsis,
  warnings,
  noWarningsText,
  hasWarnings,
  windGustSectionHeader,
  windGustPeriods,
  cloudWeatherSectionHeader,
  cloudWeatherPeriods,
  visibilitySectionHeader,
  visibilityPeriods,
  day1TempHeader,
  day1Temps,
  day2TempHeader,
  day2Temps,
  waterTempHeader,
  waterTemps,
}: MaritimeForecastRendererProps) => (
  <article className={styles.root}>
    {issuedLine && (
      <header className={styles.header}>
        <p className={styles.issuedLine}>{issuedLine}</p>
      </header>
    )}

    <section className={styles.section}>
      <p className={styles.sectionHeader}>Wind of stormwaarschuwingen</p>
      {hasWarnings ? (
        <table className={styles.dataTable}>
          <tbody>
            {warnings.map((w, i) => (
              <tr key={i}>
                <td className={styles.areaCell}>{w.areaName}</td>
                <td className={styles.dataCell}>{w.description ?? "geen"}</td>
              </tr>
            ))}
          </tbody>
        </table>
      ) : (
        <p className={styles.noData}>{noWarningsText}</p>
      )}
    </section>

    {synopsis && (
      <section className={styles.section}>
        <p className={styles.sectionHeader}>Weeroverzicht</p>
        <p className={styles.synopsis}>{synopsis}</p>
      </section>
    )}

    <section className={styles.section}>
      <p className={styles.sectionHeader}>{windGustSectionHeader}</p>
      {windGustPeriods.map((period, pi) => (
        <div key={pi} className={styles.periodBlock}>
          <p className={styles.periodHeader}>{period.header}</p>
          <table className={styles.dataTable}>
            <tbody>
              {period.rows.map((row, ri) => (
                <tr key={ri}>
                  <td className={styles.areaCell}>{row.areaName}</td>
                  <td className={styles.dataCell}>{row.text}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      ))}
    </section>

    <section className={styles.section}>
      <p className={styles.sectionHeader}>{cloudWeatherSectionHeader}</p>
      {cloudWeatherPeriods.map((period, pi) => (
        <div key={pi} className={styles.periodBlock}>
          <p className={styles.periodHeader}>{period.header}</p>
          <table className={styles.dataTable}>
            <tbody>
              {period.rows.map((row, ri) => (
                <tr key={ri}>
                  <td className={styles.areaCell}>{row.areaName}</td>
                  <td className={styles.dataCell}>{row.text}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      ))}
    </section>

    <section className={styles.section}>
      <p className={styles.sectionHeader}>{visibilitySectionHeader}</p>
      {visibilityPeriods.map((period, pi) => (
        <div key={pi} className={styles.periodBlock}>
          <p className={styles.periodHeader}>{period.header}</p>
          <table className={styles.dataTable}>
            <tbody>
              {period.rows.map((row, ri) => (
                <tr key={ri}>
                  <td className={styles.areaCell}>{row.areaName}</td>
                  <td className={styles.dataCell}>{row.text}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      ))}
    </section>

    {day1Temps.length > 0 && (
      <section className={styles.section}>
        <p className={styles.sectionHeader}>{day1TempHeader}</p>
        <table className={styles.dataTable}>
          <tbody>
            {day1Temps.map((t, i) => (
              <tr key={i}>
                <td className={styles.areaCell}>{t.areaName}</td>
                <td className={styles.dataCell}>
                  {t.min}, {t.max}
                  {t.sunrise && t.sunset && `, ${t.sunrise}, ${t.sunset}`}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </section>
    )}

    {day2Temps.length > 0 && (
      <section className={styles.section}>
        <p className={styles.sectionHeader}>{day2TempHeader}</p>
        <table className={styles.dataTable}>
          <tbody>
            {day2Temps.map((t, i) => (
              <tr key={i}>
                <td className={styles.areaCell}>{t.areaName}</td>
                <td className={styles.dataCell}>
                  {t.min}, {t.max}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </section>
    )}

    {waterTemps.length > 0 && (
      <section className={styles.section}>
        <p className={styles.sectionHeader}>{waterTempHeader}</p>
        <table className={styles.dataTable}>
          <tbody>
            {waterTemps.map((t, i) => (
              <tr key={i}>
                <td className={styles.areaCell}>{t.areaName}</td>
                <td className={styles.dataCell}>{t.value} °C</td>
              </tr>
            ))}
          </tbody>
        </table>
      </section>
    )}

    {footerLines && (
      <footer className={styles.footer}>
        {footerLines.map((line, i) => (
          <p key={i} className={styles.footerLine}>
            {line}
          </p>
        ))}
      </footer>
    )}
  </article>
);

export default MaritimeForecastRenderer;
